import api from '@/api';
import { isErrorCode } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { useCurrentUser } from './useCurrentUser';

type ProfileType = components['schemas']['ProfileType'];

export interface MemberViewerRelation {
  /** The displayed member is the viewer themselves. */
  isSelf: boolean;
  /**
   * The viewer is the study helper currently assigned to the displayed student.
   */
  isAssignedStudyHelper: boolean;
  /**
   * The viewer may open the displayed member's study-helpers tab:
   * `ASSIGNED_VIEWERS` (self, admin or assigned study helper), answered by
   * `get_student_viewer_access`.
   *
   * Two traps. It is true for a student on their own page, so it can't gate the
   * banner and must be tested after the student-self case. And it is narrower
   * than most of the supervisor sidebar's items (Course Study, Student Notes
   * and Follow-ups admit wider audiences), so it isn't the sidebar-shell test
   * either; see `$memberId.tsx`.
   */
  canSupervise: boolean;
  /**
   * The displayed member's account type. Null until the probe resolves, when
   * the member isn't a student, and when the viewer is outside
   * `ASSIGNED_VIEWERS`: the server nulls it on a refusal so the probe can't be
   * used as an existence oracle.
   */
  memberProfileType: ProfileType | null;
}

/**
 * The viewer's relation to the member whose page is open, from
 * `getStudentViewerAccess` (`can_view` and the member's `profile_type`) and
 * `getStudentStudyHelper` (the assigned helper).
 *
 * The member layout asks three distinct questions, which were once all answered
 * with `useCurrentUser().isStudyHelper && !isOwnProfile`. That folds in admins
 * and follow-up coordinators and partners, and showed the "viewing your
 * student" banner to far more viewers than assigned helpers.
 * `StudentViewerAccess` alone can't gate the banner either, since its audience
 * includes self and admins. Hence two queries and four named answers.
 */
export function useMemberViewerRelation(
  memberId: string,
): MemberViewerRelation {
  const { me, isViewingOwnProfile } = useCurrentUser();
  const studentId = Number(memberId);
  const isNumericId = Number.isFinite(studentId);

  // Same key as the one `SHStudentViewSidebar` issues, so react-query dedupes:
  // the layout and its sidebar cost one request between them.
  const { data: access } = api.getStudentViewerAccess.useQuery(
    { path: { student_id: studentId } },
    { enabled: isNumericId },
  );

  // The exact profile type, never `useCurrentUser().isStudyHelper`: legacy's
  // explorer study helpers were migrated into this one type.
  const viewerIsStudyHelper = me?.profile_type === 'STUDY_HELPER';

  // Only a study helper can be a student's assigned helper, so skipping the
  // probe for other viewers changes no answer and saves a request that can only
  // 404.
  const { data: helper } = api.getStudentStudyHelper.useQuery(
    { path: { student_id: studentId } },
    {
      enabled: isNumericId && viewerIsStudyHelper,
      // No helper assigned, not a student, and not allowed are all one 404
      // here, and all real answers, so it isn't worth retrying.
      retry: (failureCount: number, err: unknown) =>
        failureCount < 3 && !isErrorCode(err, 'resource_not_found'),
    },
  );

  return {
    isSelf: isViewingOwnProfile(memberId),
    // The bare predicate. The banner's other two conditions (`!isSelf` and the
    // viewer's profile type) are applied at the call site, where the whole rule
    // is written out in one place.
    isAssignedStudyHelper: me?.id !== undefined && helper?.id === me.id,
    canSupervise: access?.can_view === true,
    memberProfileType: access?.profile_type ?? null,
  };
}
